import React, { useState, useEffect } from 'react';
import axios from 'axios';
import { storageApi, projectSpacesApi } from '../../services/fileShareApi';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  FormControl,
  Alert,
  CircularProgress,
  TextField,
  Typography,
  Box,
} from '@mui/material';

const STORAGE_TYPE_BY_CONNECTOR = {
  MINIO_STORAGE: 'MINIO',
  AWS_S3_STORAGE: 'AWS_S3',
  EGNYTE_STORAGE: 'EGNYTE',
  GOOGLE_DRIVE_STORAGE: 'GOOGLE_DRIVE',
  SHAREPOINT_STORAGE: 'SHAREPOINT',
  DROPBOX_STORAGE: 'DROPBOX',
  STORAGE: 'EGNYTE', // generic fallback used by some tokens
};

const ProjectStorageConfig = ({ projectId, projectName, open, onClose, onSuccess }) => {
  const [loading, setLoading] = useState(false);
  const [hasStorageSetup, setHasStorageSetup] = useState(false);
  const [selectedStorageType, setSelectedStorageType] = useState('');
  const [selectedMcId, setSelectedMcId] = useState('');
  const [rootFolderPath, setRootFolderPath] = useState('');
  const [rootFolderPathLocked, setRootFolderPathLocked] = useState(false);
  const [statusInfo, setStatusInfo] = useState(null); // { storageProvider, connectorName }
  const [errorMessage, setErrorMessage] = useState('');
  const [successMessage, setSuccessMessage] = useState('');
  // The root path can only be saved once, so it must pass Test Connection first
  const [testedOkPath, setTestedOkPath] = useState(null);

  useEffect(() => {
    if (open && projectId) {
      setErrorMessage('');
      setSuccessMessage('');
      setTestedOkPath(null);
      loadDialogData();
    }
  }, [open, projectId]);

  const getAuthHeaders = () => ({
    Authorization: `Bearer ${localStorage.getItem('token')}`,
  });

  const getBackendBaseUrl = () => {
    const backendUrl = process.env.REACT_APP_BACKEND_URL || 'http://localhost:3002/';
    return backendUrl.endsWith('/') ? backendUrl.slice(0, -1) : backendUrl;
  };

  const resolveConnectorDetails = async (storageConnectorId) => {
    if (!storageConnectorId) return null;

    const token = localStorage.getItem('token');
    const tenantId = localStorage.getItem('tenantId');
    const baseUrl = getBackendBaseUrl();

    const [tokensResponse, mastersResponse] = await Promise.all([
      axios.get(`${baseUrl}/synctrack/sync-connectors/tenant/${tenantId}`, {
        headers: { Authorization: `Bearer ${token}` },
      }),
      axios.get(`${baseUrl}/synctrack/master-connector`, {
        headers: { Authorization: `Bearer ${token}` },
      }),
    ]);

    const tokens = tokensResponse.data || [];
    const masters = mastersResponse.data || [];
    const tokenRow = tokens.find(
      (t) => t.id?.toString() === storageConnectorId.toString()
    );
    if (!tokenRow) return null;

    const master = masters.find(
      (mc) =>
        mc.mcId?.toString() === tokenRow.mcId?.toString() ||
        mc.mcRecId?.toString() === tokenRow.mcId?.toString()
    );

    const connectorType = master?.mcConnectorType || null;
    const storageType = connectorType
      ? STORAGE_TYPE_BY_CONNECTOR[connectorType]
      : null;
    const connectorName =
      (tokenRow.description || '').trim() ||
      master?.mcName ||
      'Storage connector';

    return {
      mcId: tokenRow.mcId?.toString() || '',
      storageType,
      connectorName,
      storageProvider: storageType,
    };
  };

  const loadDialogData = async () => {
    try {
      setLoading(true);

      // 1) Existing project_storage_config (type + mcId + path)
      let config = null;
      try {
        const configResponse = await storageApi.getProjectStorageConfig(projectId);
        if (configResponse.data?.storageProvider) {
          config = configResponse.data;
        }
      } catch (err) {
        console.warn('No project storage config yet:', err);
      }

      // 2) Connector assigned at Create Project / Edit connectors
      let projectConnectors = {};
      try {
        const connectorsResponse = await projectSpacesApi.getConnectors(projectId);
        projectConnectors = connectorsResponse.data || {};
      } catch (err) {
        console.warn('Could not load project connectors:', err);
      }

      const storageConnectorId = projectConnectors.storageConnectorId || null;
      let resolvedFromProject = null;
      if (storageConnectorId) {
        try {
          resolvedFromProject = await resolveConnectorDetails(storageConnectorId);
        } catch (err) {
          console.warn('Could not resolve project storage connector:', err);
        }
      }

      // Prefer existing storage config; otherwise use Create Project connector
      if (config?.storageProvider) {
        let connectorName = resolvedFromProject?.connectorName || null;
        if (config.mcId && !connectorName) {
          try {
            const connectorResponse = await axios.get(
              `${getBackendBaseUrl()}/synctrack/master-connector/${config.mcId}`,
              { headers: getAuthHeaders() }
            );
            connectorName =
              connectorResponse.data.description ||
              connectorResponse.data.mcName ||
              'Storage connector';
          } catch (err) {
            connectorName = 'Storage connector';
          }
        }

        setSelectedStorageType(config.storageProvider);
        setSelectedMcId(config.mcId || resolvedFromProject?.mcId || '');
        setStatusInfo({
          storageProvider: config.storageProvider,
          connectorName,
        });
        const existingRootPath = config.rootFolderPath || '';
        setRootFolderPath(existingRootPath);
        setRootFolderPathLocked(existingRootPath.trim().length > 0);
        setHasStorageSetup(true);
      } else if (resolvedFromProject?.storageType && resolvedFromProject?.mcId) {
        // Connector chosen at Create Project — no need to pick type/connector again
        setSelectedStorageType(resolvedFromProject.storageType);
        setSelectedMcId(resolvedFromProject.mcId);
        setStatusInfo({
          storageProvider: resolvedFromProject.storageType,
          connectorName: resolvedFromProject.connectorName,
        });
        setRootFolderPath('');
        setRootFolderPathLocked(false);
        setHasStorageSetup(true);
      } else {
        // No connector on project and no storage config
        setSelectedStorageType('');
        setSelectedMcId('');
        setStatusInfo(null);
        setRootFolderPath('');
        setRootFolderPathLocked(false);
        setHasStorageSetup(false);
      }
    } catch (err) {
      console.error('Error loading storage settings:', err);
      setErrorMessage('Failed to load storage settings');
      setHasStorageSetup(false);
    } finally {
      setLoading(false);
    }
  };

  const handleSave = async () => {
    if (!selectedStorageType || !selectedMcId) {
      setErrorMessage(
        'No storage connector is linked to this project. Assign one when creating the project (or via Edit connectors), then set the root folder path here.'
      );
      return;
    }

    try {
      setLoading(true);
      setErrorMessage('');
      setSuccessMessage('');

      const normalizedRootPath = rootFolderPathLocked
        ? null
        : (rootFolderPath || '')
            .trim()
            .replace(/\\/g, '/')
            .replace(/\/+/g, '/')
            .replace(/\/$/, '') || null;
      const withLeadingSlash =
        normalizedRootPath && !normalizedRootPath.startsWith('/')
          ? `/${normalizedRootPath}`
          : normalizedRootPath;

      const configData = {
        storageType: selectedStorageType,
        mcId: selectedMcId,
        rootFolderPath: withLeadingSlash,
      };

      const response = await storageApi.configureProjectStorage(projectId, configData);

      if (response.data.rootFolderPath && !rootFolderPathLocked) {
        setRootFolderPath(response.data.rootFolderPath);
        setRootFolderPathLocked(true);
      }
      setHasStorageSetup(true);

      if (response.data.storageAccessible === false || response.data.warning) {
        setSuccessMessage('');
        setErrorMessage(
          response.data.warning ||
            response.data.message ||
            'Storage settings saved, but Egnyte/storage access failed. Check connector credentials and path.'
        );
        // Keep dialog open so the user can fix credentials
        return;
      }

      setSuccessMessage(
        rootFolderPathLocked
          ? 'Storage settings saved.'
          : 'Root folder path saved successfully.'
      );

      if (onSuccess) {
        onSuccess(response.data);
      }

      setTimeout(() => {
        onClose();
      }, 800);
    } catch (err) {
      const errorMsg = err.response?.data?.message || err.message;
      setErrorMessage('Failed to save storage settings: ' + errorMsg);
      if (errorMsg && errorMsg.includes('Root folder path cannot be changed')) {
        setErrorMessage(
          errorMsg + ' Please create a new project if you need a different root folder path.'
        );
      }
    } finally {
      setLoading(false);
    }
  };

  const handleTestConnection = async () => {
    try {
      setLoading(true);
      setErrorMessage('');
      setSuccessMessage('');
      // Before the path is locked, test what the user typed rather than the saved config
      const candidatePath = rootFolderPathLocked ? '' : (rootFolderPath || '').trim();
      const response = await storageApi.testProjectStorageConnection(
        projectId,
        candidatePath
          ? { rootFolderPath: candidatePath, storageType: selectedStorageType || '' }
          : {}
      );
      if (response.data && response.data.connected) {
        setTestedOkPath(candidatePath);
        setSuccessMessage(
          candidatePath
            ? `Connection successful — "${candidatePath}" is reachable. You can save it now.`
            : 'Connection successful — credentials and path are reachable.'
        );
      } else {
        setTestedOkPath(null);
        setErrorMessage(
          response.data?.error ||
            response.data?.message ||
            'Connection test failed. Check Egnyte connector credentials and root folder path.'
        );
      }
    } catch (err) {
      setTestedOkPath(null);
      setErrorMessage(
        'Connection test failed: ' +
          (err.response?.data?.message || err.response?.data?.error || err.message)
      );
    } finally {
      setLoading(false);
    }
  };

  const trimmedRootFolderPath = (rootFolderPath || '').trim();
  const testPassedForCurrentPath =
    testedOkPath !== null && testedOkPath === trimmedRootFolderPath;
  const testDisabled = loading || !trimmedRootFolderPath;
  const saveDisabled =
    loading ||
    !hasStorageSetup ||
    !selectedStorageType ||
    !selectedMcId ||
    rootFolderPathLocked ||
    !testPassedForCurrentPath;

  const dialogTitle = rootFolderPathLocked
    ? `Storage Settings - ${projectName}`
    : `Set Root Folder Path - ${projectName}`;

  return (
    <Dialog open={open} onClose={onClose} maxWidth="sm" fullWidth>
      <DialogTitle>{dialogTitle}</DialogTitle>

      <DialogContent>
        {loading && !statusInfo && !errorMessage && (
          <CircularProgress sx={{ display: 'block', margin: '20px auto' }} />
        )}

        {errorMessage && (
          <Alert severity="error" sx={{ mb: 2, mt: 1 }}>
            {errorMessage}
          </Alert>
        )}

        {successMessage && (
          <Alert severity="success" sx={{ mb: 2, mt: 1 }}>
            {successMessage}
          </Alert>
        )}

        {hasStorageSetup && statusInfo && (
          <Alert
            severity={errorMessage ? 'warning' : 'info'}
            sx={{ mb: 2, mt: 1 }}
          >
            <Typography variant="body2" sx={{ fontWeight: 600 }}>
              {errorMessage
                ? 'Project storage is linked — but access check failed'
                : 'Project storage is linked'}
            </Typography>
            <Box sx={{ mt: 0.5, fontSize: '0.875rem' }}>
              <div>Storage Type: {statusInfo.storageProvider}</div>
              {statusInfo.connectorName && (
                <div>Connector: {statusInfo.connectorName}</div>
              )}
              {errorMessage && (
                <Typography variant="caption" display="block" sx={{ mt: 1 }}>
                  Linking a connector is not the same as a working token. Fix Connector Token
                  params / Connect, then use Test Connection.
                </Typography>
              )}
            </Box>
          </Alert>
        )}

        {!loading && !hasStorageSetup && (
          <Alert severity="warning" sx={{ mb: 2, mt: 1 }}>
            No storage connector is linked to this project yet. Choose a storage connector when
            creating the project, or use <strong>Edit connectors</strong> (pencil icon) on the
            project card. Then return here to set the root folder path.
          </Alert>
        )}

        {hasStorageSetup && (
          <FormControl fullWidth sx={{ mb: 1 }}>
            <TextField
              label="Root Folder Path"
              value={rootFolderPath}
              onChange={(e) => {
                if (!rootFolderPathLocked) {
                  setRootFolderPath(e.target.value);
                }
              }}
              placeholder="/Shared/MyProject or Shared\MyProject"
              helperText={
                rootFolderPathLocked
                  ? 'Root folder path cannot be changed once set. Create a new project if you need a different path.'
                  : 'Use / or \\ — both work (e.g. /Shared/MyProject or Shared\\MyProject). Saved with forward slashes. Can only be set once.'
              }
              variant="outlined"
              fullWidth
              disabled={rootFolderPathLocked}
              InputProps={{
                readOnly: rootFolderPathLocked,
              }}
              sx={{
                '& .MuiInputBase-input.Mui-disabled': {
                  WebkitTextFillColor: rootFolderPathLocked ? 'rgba(0, 0, 0, 0.87)' : 'inherit',
                  backgroundColor: rootFolderPathLocked ? 'rgba(0, 0, 0, 0.04)' : 'inherit',
                },
              }}
            />
            {rootFolderPathLocked && (
              <Alert severity="info" sx={{ mt: 1 }}>
                <strong>Root folder path is locked.</strong> It has already been configured for
                this project and cannot be changed. Create a new project if you need a different
                root folder path.
              </Alert>
            )}
            {!rootFolderPathLocked && (
              <Alert severity={testPassedForCurrentPath ? 'success' : 'info'} sx={{ mt: 1 }}>
                {testPassedForCurrentPath
                  ? 'Path verified. You can save it now — it cannot be changed afterwards.'
                  : 'Enter the root folder path, then click Test Connection. Save is enabled only after the test succeeds, because the path cannot be changed once saved.'}
              </Alert>
            )}
          </FormControl>
        )}
      </DialogContent>

      <DialogActions>
        <Button onClick={onClose}>Cancel</Button>
        {hasStorageSetup && (
          <Button onClick={handleTestConnection} disabled={testDisabled}>
            Test Connection
          </Button>
        )}
        {rootFolderPathLocked || !hasStorageSetup ? (
          <Button onClick={onClose} variant="contained">
            Done
          </Button>
        ) : (
          <Button onClick={handleSave} variant="contained" disabled={saveDisabled}>
            Save Root Path
          </Button>
        )}
      </DialogActions>
    </Dialog>
  );
};

export default ProjectStorageConfig;
